import { useLayoutEffect, useMemo, useEffect } from 'react'
import { MantineProvider } from '@mantine/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { authUserAtom } from '../atoms/authAtoms'
import { gameSettingsAtom } from '../atoms/playerAtoms'
import { activeStoryThemeAtom, customThemesAtom } from '../atoms/themeAtoms'
import { themeService } from '../services/themeService'
import { resolveTheme } from './resolveTheme'

/**
 * Live theming layer. Sits inside the app providers, reads the three theme
 * inputs reactively, resolves them to a Mantine theme + CSS variables, applies
 * the vars to <html>, and renders MantineProvider. Any change (player toggles
 * high-contrast, picks a size, enters a story with a theme) re-resolves and the
 * UI updates without a reload.
 */
export function ThemedApp({ children }) {
  const user = useAtomValue(authUserAtom)
  const gameSettings = useAtomValue(gameSettingsAtom)
  const storyThemeId = useAtomValue(activeStoryThemeAtom)
  const customThemes = useAtomValue(customThemesAtom)
  const setCustomThemes = useSetAtom(customThemesAtom)

  // Fetch public custom themes once and key them by slug + id so a story's
  // options.theme (which may reference either) resolves. Failure is non-fatal —
  // the resolver falls back to Novastar for unknown ids.
  const { data: publicThemes } = useQuery({
    queryKey: ['publicThemes'],
    queryFn: () => themeService.getPublicThemes(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })
  useEffect(() => {
    if (!Array.isArray(publicThemes)) return
    const byKey = {}
    for (const t of publicThemes) {
      const entry = { name: t.name, ...(t.config || {}) }
      if (t.slug) byKey[t.slug] = entry
      if (t.id != null) byKey[String(t.id)] = entry
    }
    setCustomThemes(byKey)
  }, [publicThemes, setCustomThemes])

  // UserSerializer exposes a11y at the top level (derived from options.a11y).
  const userA11y = user?.a11y || {}
  const sizePref = gameSettings?.fontSize || 'medium'

  const { mantineTheme, cssVars, colorScheme, reducedMotion } = useMemo(
    () => resolveTheme({ storyThemeId, userA11y, sizePref, customThemes }),
    // userA11y is an object — depend on its fields, not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      storyThemeId,
      sizePref,
      customThemes,
      userA11y.high_contrast,
      userA11y.reduced_motion,
      userA11y.dyslexia_font,
      userA11y.min_text_size,
    ],
  )

  // Write CSS vars + the motion flag onto <html> so body (gradient) and every
  // var() consumer pick them up. useLayoutEffect avoids a flash of the old theme.
  useLayoutEffect(() => {
    const root = document.documentElement
    for (const [key, value] of Object.entries(cssVars)) {
      root.style.setProperty(key, value)
    }
    root.setAttribute('data-bw-motion', reducedMotion ? '0' : '1')
  }, [cssVars, reducedMotion])

  return (
    <MantineProvider theme={mantineTheme} forceColorScheme={colorScheme}>
      {children}
    </MantineProvider>
  )
}
